import { Component } from "react";
import { UserContext } from "../UserContext";
import { withRouter } from "../utils/withRouter";
import api from "../api/index";
import { Modal, Table, message } from "antd";
import { DateTime } from "luxon";
import LoadingComponent from "./LoadingComponent";

class ManageLicenses extends Component {
  static contextType = UserContext;

  constructor(props) {
    super(props);
    this.state = {
      dataLoading: true,
      actionLoading: false,
      licenses: [],
      selectedLicenseIds: [],
      showScheduledOnly: false,
      locationFilter: "all",
    };
  }

  componentDidMount() {
    this.loadLicenseData();
  }

  normalizeLicenseIds = (list) => {
    if (!Array.isArray(list)) return [];
    return list
      .map((item) => (typeof item === "object" && item ? item.license_id : item))
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value));
  };

  loadLicenseData = async () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const summaryUrl =
        `${api.main}api/billing/licenses/summary?sagent_id=${this.context.user.id}` +
        `&access_token=${encodeURIComponent(this.context.user.access_token)}`;
      const locationsUrl =
        `${api.main}api/billing/locations?sagent_id=${this.context.user.id}` +
        `&tz=${encodeURIComponent(tz)}` +
        `&access_token=${encodeURIComponent(this.context.user.access_token)}`;

      const [summaryResp, locationsResp] = await Promise.all([
        fetch(summaryUrl, { headers: { "Content-Type": "application/json" } }),
        fetch(locationsUrl, { headers: { "Content-Type": "application/json" } }),
      ]);

      const summaryData = await summaryResp.json();
      const locationsData = await locationsResp.json();

      const licensedLocations = locationsData.licensed_locations || [];
      const availableLocations = locationsData.available_locations || [];

      const licensedByLicenseId = new Map(
        licensedLocations.map((loc) => [Number(loc.license_id), loc])
      );

      const locationsById = new Map();
      [...licensedLocations, ...availableLocations].forEach((loc) => {
        const id = Number(loc.registration_id);
        if (Number.isFinite(id)) locationsById.set(id, loc);
      });

      const assignments = Array.isArray(summaryData.assignments)
        ? summaryData.assignments
        : [];
      const assignedIds = this.normalizeLicenseIds(assignments.map((a) => a.license_id));
      const availableIds = this.normalizeLicenseIds(summaryData.available_license_ids || []);
      const scheduledCancelIds = this.normalizeLicenseIds(
        summaryData.scheduled_cancel_license_ids || []
      );
      const licensedIds = this.normalizeLicenseIds(licensedLocations.map((loc) => loc.license_id));
      const allLicenseIds = Array.from(new Set([...assignedIds, ...availableIds, ...licensedIds]));
      const scheduledCancelSet = new Set(scheduledCancelIds);

      const assignmentMap = new Map(
        assignments
          .map((a) => [Number(a.license_id), Number(a.location_id)])
          .filter(([licenseId, locationId]) => Number.isFinite(licenseId))
      );

      const licenses = allLicenseIds.map((licenseId) => {
        const licensed = licensedByLicenseId.get(licenseId) || null;
        const locationId = assignmentMap.get(licenseId) || licensed?.registration_id || null;
        const locationInfo = locationId ? locationsById.get(locationId) : null;
        return {
          license_id: licenseId,
          location_id: locationId,
          location_name: locationInfo?.name || licensed?.name || "Unassigned",
          pending_cancellation:
            licensed?.pending_cancellation || (scheduledCancelSet.has(licenseId) ? 1 : 0),
          cancel_effective_at_local: licensed?.cancel_effective_at_local || null,
        };
      });

      this.setState({
        licenses,
        dataLoading: false,
      });
    } catch (error) {
      this.setState({ dataLoading: false });
      Modal.error({
        title: "Failed to load licenses",
        content: error.message || "Please try again.",
      });
    }
  };

  formatDate = (value, tz = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
    if (!value) return null;
    const dt = DateTime.fromISO(value, { zone: tz });
    return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : value;
  };

  getNextCancelDateIso = (tz = Intl.DateTimeFormat().resolvedOptions().timeZone) => {
    const endOfMonth = DateTime.now().setZone(tz).endOf("month");
    return endOfMonth.plus({ days: 1 }).startOf("day").toISODate();
  };

  getSelectedBuckets = () => {
    const { licenses, selectedLicenseIds } = this.state;
    const selected = licenses.filter((row) => selectedLicenseIds.includes(row.license_id));
    return {
      cancelable: selected.filter((row) => Number(row.pending_cancellation) !== 1),
      undoable: selected.filter((row) => Number(row.pending_cancellation) === 1),
    };
  };

  scheduleCancellation = () => {
    const { cancelable } = this.getSelectedBuckets();
    if (cancelable.length === 0) {
      Modal.error({
        title: "Invalid Selection",
        content: "Select at least one active license to cancel.",
      });
      return;
    }

    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const endOfMonth = DateTime.now().setZone(tz).endOf("month");
    const firstOfNextMonth = endOfMonth.plus({ days: 1 }).startOf("day");
    const firstOfNextMonthLabel = firstOfNextMonth.toLocaleString(DateTime.DATE_MED);

    Modal.confirm({
      title: "Schedule end-of-month cancellation?",
      content: (
        <div style={{ whiteSpace: "pre-line" }}>
          {`Cancellation will take effect on ${firstOfNextMonthLabel}. You can undo anytime before then.`}
        </div>
      ),
      okText: "Schedule Cancellations",
      cancelText: "Keep Licenses",
      okType: "danger",
      onOk: () =>
        new Promise(async (resolve, reject) => {
          try {
            this.setState({ actionLoading: true });
            const resp = await fetch(`${api.main}api/billing/licenses/cancel`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                sagent_id: this.context.user.id,
                license_ids: cancelable.map((row) => row.license_id),
                tz,
                access_token: this.context.user.access_token,
              }),
            });

            const result = await resp.json();
            if (!resp.ok || !result.ok) throw new Error(result?.error || "Cancellation failed");

            await this.loadLicenseData();
            this.setState({ selectedLicenseIds: [] });
            message.success("Cancellation scheduled successfully.");
            resolve();
          } catch (error) {
            Modal.error({
              title: "Cancellation Failed",
              content: error.message || "Please try again.",
            });
            reject(error);
          } finally {
            this.setState({ actionLoading: false });
          }
        }),
    });
  };

  undoCancellation = async () => {
    const { undoable } = this.getSelectedBuckets();
    if (undoable.length === 0) {
      Modal.error({
        title: "Invalid Selection",
        content: "Select at least one scheduled cancellation to undo.",
      });
      return;
    }

    try {
      this.setState({ actionLoading: true });
      const resp = await fetch(`${api.main}api/billing/licenses/cancel/undo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sagent_id: this.context.user.id,
          license_ids: undoable.map((row) => row.license_id),
          access_token: this.context.user.access_token,
        }),
      });

      const result = await resp.json();
      if (!resp.ok || !result.ok) throw new Error(result?.error || "Undo failed");

      await this.loadLicenseData();
      this.setState({ selectedLicenseIds: [] });
      message.success("Cancellation undone successfully.");
    } catch (error) {
      Modal.error({
        title: "Undo Failed",
        content: error.message || "Please try again.",
      });
    } finally {
      this.setState({ actionLoading: false });
    }
  };

  render() {
    const {
      dataLoading,
      licenses,
      selectedLicenseIds,
      showScheduledOnly,
      actionLoading,
      locationFilter,
    } =
      this.state;

    if (dataLoading) {
      return (
        <div className="loading billing-loading">
          <LoadingComponent />
        </div>
      );
    }

    const filteredLicenses = licenses
      .filter((row) =>
        showScheduledOnly ? Number(row.pending_cancellation) === 1 : true
      )
      .filter((row) => {
        if (locationFilter === "assigned") return Boolean(row.location_id);
        if (locationFilter === "unassigned") return !row.location_id;
        return true;
      });

    const { cancelable, undoable } = this.getSelectedBuckets();

    return (
      <div className="billing-page">
        <div className="page-header">
          <div>
            <p className="page-eyebrow">Licenses</p>
            <h2>Manage Licenses</h2>
            <p className="section-subtitle">
              Review license assignments and schedule cancellations.
            </p>
          </div>
        </div>

        <section className="page-section billing-panel">
          <div className="billing-content-card">
            <div
              className="bulk-actions"
              style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}
            >
              <button
                className="btn btn-primary"
                onClick={() => this.setState({ selectedLicenseIds: [] })}
                disabled={selectedLicenseIds.length === 0 || actionLoading}
              >
                Clear
              </button>
              <span style={{ marginLeft: 8 }}>Selected: {selectedLicenseIds.length}</span>
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 12 }}>
                <span>Filter:</span>
                <select
                  value={locationFilter}
                  onChange={(e) => this.setState({ locationFilter: e.target.value })}
                  className="input"
                  style={{ minWidth: 160 }}
                >
                  <option value="all">All licenses</option>
                  <option value="assigned">With locations</option>
                  <option value="unassigned">Without locations</option>
                </select>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 6, marginLeft: 12 }}>
                <input
                  type="checkbox"
                  checked={showScheduledOnly}
                  onChange={(e) => this.setState({ showScheduledOnly: e.target.checked })}
                />
                Show scheduled cancellations only
              </label>
              <div style={{ flex: 1 }} />
              <button
                className="btn btn-danger"
                onClick={this.scheduleCancellation}
                disabled={cancelable.length === 0 || actionLoading}
                title="Schedule end-of-month cancellation"
              >
                {actionLoading ? "Updating..." : "Cancel Licenses"}
              </button>
              <button
                className="btn btn-primary"
                onClick={this.undoCancellation}
                disabled={undoable.length === 0 || actionLoading}
                title="Undo scheduled cancellations"
              >
                {actionLoading ? "Updating..." : "Undo Cancellation"}
              </button>
            </div>

            <div className="billing-table-wrapper show-scrollbar transfer-table-grid">
              <Table
                className="manage-locations-table"
                size="middle"
                rowKey="license_id"
                dataSource={filteredLicenses}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: false,
                  className: "billing-table-pagination",
                  showTotal: (total, range) =>
                    total > 0 ? `${range[0]}-${range[1]} of ${total}` : "0-0 of 0",
                }}
                rowSelection={{
                  selectedRowKeys: selectedLicenseIds,
                  onChange: (keys) =>
                    this.setState({ selectedLicenseIds: keys.map((key) => Number(key)) }),
                }}
                columns={[
                  {
                    title: "License ID",
                    dataIndex: "license_id",
                    width: 140,
                    sorter: (a, b) => Number(a.license_id) - Number(b.license_id),
                  },
                  {
                    title: "Location",
                    dataIndex: "location_name",
                    sorter: (a, b) => (a.location_name || "").localeCompare(b.location_name || ""),
                    render: (_, record) => (
                      <div className="manage-location-cell">
                        <div className="manage-location-name">{record.location_name}</div>
                        {record.location_id ? (
                          <div className="manage-location-address">ID {record.location_id}</div>
                        ) : (
                          <div className="manage-location-address">Unassigned</div>
                        )}
                      </div>
                    ),
                  },
                  {
                    title: "Status",
                    key: "status",
                    render: (_, record) => {
                      const isPending = Number(record.pending_cancellation) === 1;
                      const cancelDate =
                        record.cancel_effective_at_local || this.getNextCancelDateIso();
                      const date = this.formatDate(isPending ? cancelDate : null);
                      return isPending ? (
                        <span className="manage-location-status manage-location-status--transfer">
                          Scheduled{date ? ` • ${date}` : ""}
                        </span>
                      ) : (
                        <span className="manage-location-status">Active</span>
                      );
                    },
                  },
                ]}
              />
            </div>
          </div>
        </section>
      </div>
    );
  }
}

export default withRouter(ManageLicenses);
